import { type FC, Suspense, useEffect } from 'react';
import { useQueryLoader } from 'react-relay';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';

import {
    pdpSharedRecentlyFavoritedRootQuery,
    type pdpSharedRecentlyFavoritedRootQueryType,
} from '../../../queries/pdpSharedRecentlyFavoritedRoot.ts';
import { PdpSharedDivider } from '../PdpSharedDivider/PdpSharedDivider.tsx';
import { PdpRespMarketplaceLoadingCarousel } from '../../respMarketplace/PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel.tsx';
import { PdpMobileMarketplaceLoadingCarousel } from '../../mobileMarketplace/PdpMobileMarketplaceLoadingCarousel/PdpMobileMarketplaceLoadingCarousel.tsx';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

const PdpSharedRecentlyFavoritedCarouselLazy = dibsLazy(
    'PdpSharedRecentlyFavoritedCarousel',
    () =>
        import(
            /* webpackChunkName: "PdpSharedRecentlyFavoritedCarousel" */ './PdpSharedRecentlyFavoritedCarousel.tsx'
        )
);

type Props = {
    userId: string;
    itemId: string;
    isTrade: boolean;
    priceBookName: string;
    fetchUser: boolean;
    currency: string;
};

const LoadingCarousel: FC = () => {
    const { isMobile } = useServerVarsContext();
    return isMobile ? (
        <>
            <PdpSharedDivider isViewportWidth size="xSmall" />
            <PdpMobileMarketplaceLoadingCarousel />
        </>
    ) : (
        <PdpRespMarketplaceLoadingCarousel />
    );
};

export const PdpSharedRecentlyFavoritedCarouselRenderer: FC<Props> = ({
    userId,
    itemId,
    isTrade,
    priceBookName,
    fetchUser,
    currency,
}) => {
    const [queryRef, loadRelayQuery] = useQueryLoader<pdpSharedRecentlyFavoritedRootQueryType>(
        pdpSharedRecentlyFavoritedRootQuery
    );

    useEffect(() => {
        if (!userId) {
            return;
        }
        loadRelayQuery({
            userId,
            itemId,
            isTrade,
            priceBookName,
            fetchUser,
        });
    }, [loadRelayQuery, userId, itemId, isTrade, priceBookName, fetchUser]);

    if (!userId) {
        return null;
    }

    return queryRef ? (
        <Suspense fallback={<LoadingCarousel />}>
            <PdpSharedRecentlyFavoritedCarouselLazy queryRef={queryRef} currency={currency} />
        </Suspense>
    ) : (
        <LoadingCarousel />
    );
};
